/*
 * This file is part of Invenio.
 * Copyright (C) 2016-2021 CERN.
 * Copyright (C) 2023 Northwestern University.
 *
 * Modified by Mesh Research as part of Knowledge Commons Works.
 * Copyright (C) 2024-2025 Mesh Research.
 *
 * Invenio is free software; you can redistribute it and/or modify it
 * under the terms of the MIT License; see LICENSE file for more details.
 */

import React from "react";
import ReactDOM from "react-dom";
import { http } from "react-invenio-forms";

import CommunitiesCardGroup from "./CommunitiesCardGroup";

/*
 * Render the static sections that are always present regardless of vocabulary
 * configuration (authenticated-user collections and newest collections).
 */
const renderStaticSections = () => {
  const userCommunitiesContainer = document.getElementById("user-communities");
  const newCommunitiesContainer = document.getElementById("new-communities");

  const staticSections = [
    {
      container: userCommunitiesContainer,
      fetchDataUrl: "/api/user/communities?q=&sort=newest&page=1&size=5",
      emptyMessage: "You are not yet a member of any collection.",
      defaultLogo: "/static/images/square-placeholder.png",
    },
    {
      container: newCommunitiesContainer,
      fetchDataUrl: "/api/communities?q=&sort=newest&page=1&size=5",
      emptyMessage: "There are no new collections.",
      defaultLogo: "/static/images/square-placeholder.png",
    },
  ];

  staticSections.forEach((section) => {
    if (section.container) {
      ReactDOM.render(
        <CommunitiesCardGroup
          fetchDataUrl={section.fetchDataUrl}
          emptyMessage={section.emptyMessage}
          defaultLogo={section.defaultLogo}
        />,
        section.container
      );
    }
  });
};

/*
 * Fetch community types from the vocabularies API and dynamically create
 * a section for each type inside the #dynamic-community-type-sections
 * container.
 */
const renderDynamicSections = async () => {
  const wrapper = document.getElementById("dynamic-community-type-sections");
  if (!wrapper) return;

  try {
    const response = await http.get(
      "/api/vocabularies/communitytypes?size=100"
    );
    const types = response?.data?.hits?.hits ?? [];

    types.forEach((vocabRecord) => {
      const typeId = vocabRecord.id;
      const titleEn =
        vocabRecord?.title?.en ?? vocabRecord?.title_l10n ?? typeId;

      // Build the DOM nodes that mirror the static sections layout.
      const row = document.createElement("div");
      row.className = "ui row";

      const col = document.createElement("div");
      col.className = "ui column sixteen wide pl-0 pr-0";

      const header = document.createElement("h2");
      header.className = "ui header mb-0";
      header.textContent = `${titleEn} collections`;

      const seeAll = document.createElement("a");
      seeAll.className = "rel-ml-1";
      seeAll.href = `/collections/search?q=metadata.type.id:${encodeURIComponent(
        typeId
      )}`;
      seeAll.textContent = "See all";

      col.appendChild(header);
      col.appendChild(seeAll);
      row.appendChild(col);

      const cardContainer = document.createElement("div");
      cardContainer.className =
        "rel-mt-2 ui column sixteen wide pl-0 pr-0";
      row.appendChild(cardContainer);

      wrapper.parentNode.insertBefore(row, wrapper);

      // Render the React card group into the freshly created container.
      ReactDOM.render(
        <CommunitiesCardGroup
          fetchDataUrl={`/api/communities?q=metadata.type.id:${encodeURIComponent(
            typeId
          )}&sort=newest&page=1&size=5`}
          emptyMessage={`There are no ${titleEn.toLowerCase()} collections.`}
          defaultLogo="/static/images/square-placeholder.png"
        />,
        cardContainer
      );
    });
  } catch (error) {
    console.error("Failed to load community types:", error);
  }
};

// Kick off rendering.
renderStaticSections();
renderDynamicSections();